import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Permission, RoleResponse } from '@/api/role';
import { OrderStatus } from '@/api/orders';
import { userStatus } from '@/api/employee';
import { supplierStatus } from '@/api/suppliers';
import { GiftStatus } from '@/api/gifts';
import { GiftRequestStatus } from '@/api/gifts-requests';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDateWithTime(date: string) {
  if (!date) return '----—';
  return format(new Date(date), 'HH:mm dd/MM/yyyy', { locale: vi });
}

export function formatDate(date: string) {
  if (!date) return '----—';
  return format(new Date(date), 'dd/MM/yyyy', { locale: vi });
}

export function renderGender(gender: string) {
  return gender === 'male' ? 'Nam' : gender === 'female' ? 'Nữ' : 'Khác';
}

export function renderMaritalStatus(maritalStatus: string) {
  return maritalStatus === 'single'
    ? 'Độc thân'
    : maritalStatus === 'married'
    ? 'Đã kết hôn'
    : 'Khác';
}

export function checkIsPermissionWithScreen(
  screen: string,
  action: string,
  permission?: Permission[]
) {
  return permission?.some(
    (p: Permission) =>
      p.screen === screen &&
      p.access.includes(
        action as
          | 'create'
          | 'read'
          | 'update'
          | 'delete'
          | 'approval'
          | 'low_approval'
          | 'high_approval'
      )
  );
}

export function checkIsPermisstionApprove(
  role?: RoleResponse,
  status?: OrderStatus
) {
  const isApprove = role?.permissions.some(
    (p: Permission) => p.screen === 'orders' && p.access.includes('approval')
  );

  return isApprove && status === OrderStatus.PENDING;
}

export function checkIsPermisstionApproveLow(
  role?: RoleResponse,
  status?: GiftRequestStatus
) {
  const isApproveLow = role?.permissions.some(
    (p: Permission) =>
      p.screen === 'gifts-requests' && p.access.includes('low_approval')
  );

  return isApproveLow && status === GiftRequestStatus.PENDING;
}

export function checkIsPermisstionApproveHigh(
  role?: RoleResponse,
  status?: GiftRequestStatus
) {
  const isApproveHigh = role?.permissions.some(
    (p: Permission) =>
      p.screen === 'gifts-requests' && p.access.includes('high_approval')
  );

  return isApproveHigh && status === GiftRequestStatus.PENDING;
}

export function checkIsRoleEditOrders(status?: OrderStatus) {
  return (
    status === OrderStatus.NEW ||
    status === OrderStatus.ACCOUNTING_REJECTED ||
    status === OrderStatus.GENERAL_DIRECTOR_REJECTED ||
    status === OrderStatus.ADMIN_REJECTED ||
    status === OrderStatus.REJECTED
  );
}

export function checkIsRoleEditGiftRequests(status?: GiftRequestStatus) {
  return (
    status === GiftRequestStatus.NEW ||
    status === GiftRequestStatus.BUSINESS_REJECTED ||
    status === GiftRequestStatus.GENERAL_DIRECTOR_REJECTED ||
    status === GiftRequestStatus.ADMIN_REJECTED ||
    status === GiftRequestStatus.REJECTED
  );
}

export function checkIsRoleDeleteOrders(status?: OrderStatus) {
  return status === OrderStatus.NEW;
}

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};
export const formatCurrencyWithoutSymbol = (amount: number) => {
  if (!amount && amount !== 0) return '';

  return new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatNumber = (num: number) => {
  return new Intl.NumberFormat('vi-VN').format(num);
};

export const renderUserStatus = (status: userStatus) => {
  const statusMap = {
    [userStatus.ACTIVE]: {
      label: 'Hoạt động',
      className: 'text-green-500',
    },
    [userStatus.INACTIVE]: {
      label: 'Không hoạt động',
      className: 'text-red-500',
    },
  };
  return statusMap[status];
};

export const renderStatusText = (status: supplierStatus | GiftStatus) => {
  const statusMap = {
    [supplierStatus.ACTIVE]: {
      label: 'Hoạt động',
      className: 'text-green-500',
    },
    [supplierStatus.INACTIVE]: {
      label: 'Không khả dụng',
      className: 'text-red-500',
    },
  };
  return statusMap[status];
};
